/**
 * generationScenarioDraftMemory — crash/reload protection for unsaved
 * Generation & Impact scenario edits.
 *
 * The workbench held `input` in plain `useState`, so a reload discarded every
 * edit since the last explicit Save. This keeps a local draft of the working
 * input so the work survives a refresh, a crash, or an accidental navigation.
 *
 * What this is NOT: a save. The scenario record in Firestore remains the only
 * shared, immutable, publishable artifact — a restored draft is still unsaved
 * and still blocks publishing until the user saves it. This only stops the
 * browser from being the thing that loses work.
 *
 * Safety rules that matter here:
 *  - Keyed by `${siteId}:${scenarioId}` so a draft can never be restored onto
 *    a different site or a different saved scenario.
 *  - Stores the fingerprint the draft diverged FROM. If the underlying saved
 *    scenario changed elsewhere since, the caller can tell (the workbench
 *    already surfaces a "changed elsewhere" conflict) instead of silently
 *    reviving a draft against a moved base.
 *  - Versioned + validated + silent-fail, with an in-memory fallback, exactly
 *    like the other memory modules in this codebase.
 */
import type { GenerationImpactScenarioInput } from '../../../types/generationImpact';

export interface SavedScenarioDraft {
  /** The working scenario input at the time of the write. */
  input: GenerationImpactScenarioInput;
  /** Fingerprint of the SAVED base this draft diverged from ('draft' if none). */
  baseFingerprint: string;
  /** ISO timestamp, surfaced so the UI can say how old the recovered work is. */
  savedAt: string;
}

const KEY_PREFIX = 'siterag:generationScenarioDraft:';
const VERSION = 1;
const PROBE_KEY = `${KEY_PREFIX}__probe__`;

/* ── Storage facade (localStorage with in-memory fallback) ────────────── */

let memoryStore: Map<string, string> | null = null;
let probed: boolean | null = null;

function localStorageUsable(): boolean {
  if (probed !== null) return probed;
  try {
    window.localStorage.setItem(PROBE_KEY, '1');
    probed = window.localStorage.getItem(PROBE_KEY) === '1';
    window.localStorage.removeItem(PROBE_KEY);
  } catch {
    probed = false;
  }
  return probed;
}

function rawGet(key: string): string | null {
  if (typeof window !== 'undefined' && localStorageUsable()) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }
  return memoryStore?.get(key) ?? null;
}

function rawSet(key: string, value: string): void {
  if (typeof window !== 'undefined' && localStorageUsable()) {
    try {
      window.localStorage.setItem(key, value);
      return;
    } catch {
      /* quota — fall through to memory so the session still recovers */
    }
  }
  (memoryStore ??= new Map()).set(key, value);
}

function rawRemove(key: string): void {
  memoryStore?.delete(key);
  if (typeof window !== 'undefined' && localStorageUsable()) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* swallow */
    }
  }
}

function rawClearPrefix(prefix: string): void {
  memoryStore?.clear();
  if (typeof window === 'undefined' || !localStorageUsable()) return;
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i -= 1) {
      const k = window.localStorage.key(i);
      if (k?.startsWith(prefix)) window.localStorage.removeItem(k);
    }
  } catch {
    /* swallow */
  }
}

/** Draft identity — a draft is only ever restored onto the same site+scenario. */
export function scenarioDraftKey(siteId: string, scenarioId: string): string {
  return `${siteId}:${scenarioId}`;
}

export function readScenarioDraft(key: string): SavedScenarioDraft | null {
  if (!key) return null;
  try {
    const raw = rawGet(KEY_PREFIX + key);
    if (!raw) return null;
    const data = JSON.parse(raw) as Record<string, unknown>;
    if (data.v !== VERSION) return null;
    const input = data.input;
    // A scenario input is only usable if it still carries its own identity;
    // anything malformed is dropped rather than restored over good state.
    if (!input || typeof input !== 'object' || Array.isArray(input)) return null;
    if (!Array.isArray((input as { units?: unknown }).units)) return null;
    const baseFingerprint = typeof data.baseFingerprint === 'string' ? data.baseFingerprint : '';
    const savedAt = typeof data.savedAt === 'string' ? data.savedAt : '';
    if (!savedAt) return null;
    return {
      input: input as GenerationImpactScenarioInput,
      baseFingerprint,
      savedAt,
    };
  } catch {
    return null;
  }
}

export function writeScenarioDraft(
  key: string,
  input: GenerationImpactScenarioInput,
  baseFingerprint: string,
): void {
  if (!key) return;
  try {
    rawSet(KEY_PREFIX + key, JSON.stringify({
      v: VERSION,
      input,
      baseFingerprint,
      savedAt: new Date().toISOString(),
    }));
  } catch {
    /* a scenario too large to serialise must not break editing */
  }
}

/** Clear the draft — call once the work is genuinely saved. */
export function clearScenarioDraft(key: string): void {
  if (!key) return;
  rawRemove(KEY_PREFIX + key);
}

/** Test hook — remove every saved draft (both backends). */
export function clearScenarioDraftsForTests(): void {
  rawClearPrefix(KEY_PREFIX);
}
